<template>
    <b-modal id="issue-certificate-modal" @change="onModalClose"
      :dialog-class="'animate-modal-dialog ' + (isExpanded ? 'expanded' : 'shrink')" :visible="visible" hide-header
      centered content-class="issue-certificate-modal" size="xl" body-class="modal-body" hide-footer
      modal-class="custom-modal" no-close-on-backdrop>
  
      <div class="body-container">
        <div class="right-close-panel">
          <button @click="onModalClose" class="close-button" role="button">
            <img src="@/assets/images/cancel-icon.svg" />
          </button>
        </div>
  
        <div class="modal-content-wrapper">

          <div v-if="isGenerating" class="generating-overlay">
            <div class="generating-content">
              <div class="loading-icon-container">
                <b-spinner v-if="!isCompleted" class="generating-spinner" variant="success" label="Generating..."></b-spinner>
                <div v-else class="tick-icon"><i class="fas fa-check"></i></div>
              </div>
              <h3 class="generating-title">
                {{ isCompleted ? 'Certificates Generated Successfully!' : 'Generating Certificates...' }}
              </h3>
              <p class="generating-subtitle">
                {{ isCompleted ? 'Your certificates have been created and are ready.' : 'Please wait while we generate your certificates.' }}
              </p>
            </div>
          </div>
  
          <div v-else class="step-sidebar">
            <div class="step-item" @click="jumpToStep(1)"
              :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
              <div class="step-circle">1</div>
              <div class="step-content">
                <h3 class="step-title">Upload Excel</h3>
                <p class="step-description">Upload file with emails</p>
              </div>
            </div>
            <div class="step-item" @click="jumpToStep(2)"
              :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
              <div class="step-circle">2</div>
              <div class="step-content">
                <h3 class="step-title">Certificate Previews</h3>
                <p class="step-description">Review user certificates</p>
              </div>
            </div>
          </div>
  
          <div class="main-content">
            <div class="info-container expand">
              <h1 class="modal-title">Issue Certificates</h1>
  
              <div v-show="currentStep === 1" class="step-content">
                <div class="upload-container-wrapper" v-show="!isGenerating">
                  <div class="upload-container">
                    <div class="upload-section">
                      <vue-dropzone ref="excelDropzone" id="issue-certificate-modal-dropzone"
                        :options="dropzoneOptions" @vdropzone-file-added="onFileAdded"
                        @vdropzone-removed-file="onFileRemoved" @vdropzone-success="onUploadSuccess"
                        @vdropzone-error="onUploadError" @vdropzone-sending="onUploadStart"
                        class="template-dropzone big-dropzone">
                      </vue-dropzone>
  
                      <p class="allowed-formats">
                        Allowed formats: <strong class="text-danger">CSV, XLSX</strong>
                      </p>
  
                      <div v-if="invalidUserEmails.length > 0" class="error-row mb-4">
                        <button v-if="invalidUserEmails.length > 0" @click="copyInvalidEmails"
                          class="copy-btn">Copy Invalid Emails</button>
                      </div>
                    </div>
                  </div>

                  <div class="form-check form-switch mt-1 mb-4">
                    <input class="form-check-input" type="checkbox" role="switch" id="sendEmail" name="sendEmail" v-model="sendEmail" />
                    <label class="form-check-label" for="sendEmail">
                                    {{ sendEmail ? 'Yes, send emails' : 'No, don’t send emails' }}
                    </label>
                  </div>
  
                  <div v-if="!selectedFile" class="template-info">
                    <h5>Excel File Requirements</h5>
                    <p>
                      Upload an Excel or CSV file containing at least the <code>Email</code> column.
                      Other columns can be used as placeholders in the certificate template.
                      Use <strong>PascalCase</strong> for column names, e.g., <code>UserFullName</code>,
                      <code>CourseName</code>, <code>Module</code>.
                      Click <a href="/issue-certificates-sample.csv" download class="download-sample-link">download</a> to get sample template.
                    </p>

                    <h5>Certificate Required Columns</h5>
                    <ul class="placeholder-list mt-2">
                      <li class="placeholder-item">
                        <span>1. </span><code>{{'Email'}}</code>
                      </li>
                      <li v-for="(ph, idx) in placeholders" class="placeholder-item" :key="idx">
                       <span>{{idx + 2}}. </span><code>{{ph}}</code>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
  
              <div v-show="currentStep === 2" class="step-content">
                <ol class="preview-list">
                  <li v-for="(row, i) in formattedRows" :key="i">
                    <h5 style="display: inline-block;">{{row.email }}</h5> 
                    <span v-if="!row.userExists" class="user-not-found">
                      (Not Found)
                    </span>
  
                    <div v-if="isLoadingUsers" class="shimmer-card"></div>
  
                    <CertificatePreview 
                      v-else-if="row.userExists" class="mt-1 mb-4"
                      :scaleX="0.4" :scaleY="0.3"
                      :templateContent="templateContent"
                      :placeholders="buildUserPlaceholders(row)"
                      @onOverflowChange="(val) => handleOverflowChange(row.email, val)" />
                  </li>
                </ol>
              </div>
            </div>
  
            <div class="step-btn-container">
              <div class="step-navigation">
                <button v-if="currentStep > 1" @click="previousStep" class="step-btn secondary">Previous</button>
                <button v-if="currentStep < totalSteps" :disabled="!isCurrentStepValid" @click="nextStep" class="step-btn">
                  Next <img src="@/assets/images/arrow-right.svg" />
                </button>
                <button v-if="currentStep === totalSteps" :disabled="excelData.length === 0 || isLoadingUsers"
                  @click="handleFinish" class="step-btn">
                  Finish <img src="@/assets/images/arrow-right.svg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </template>
  
  <script>
  import { CERTIFICATE_GENERATE_API } from "../../../constants/urls";
  import { mapState, mapActions } from "vuex";
  import CertificatePreview from "./CertificatePreview.vue";
  import CookieHelpers from "../../../helpers/CookieHelpers";
  import vue2Dropzone from "vue2-dropzone";
  import 'vue2-dropzone/dist/vue2Dropzone.min.css';
  
  export default {
    name: 'IssueCertificatesModal',
    components: { vueDropzone: vue2Dropzone, CertificatePreview },
    props: { certificate: Object, visible: Boolean },
    data() {
      return {
        isExpanded: false,
        currentStep: 1,
        totalSteps: 2,
        excelData: [],
        placeholders: [],
        users: [],               
        selectedFile: null,
        invalidUserEmails: [],
        isLoadingPreview: false,
        isLoadingUsers: false,  
        templateContent: null,
        overflowStates: {},
        sendEmail: false,
        isGenerating: false,
        isCompleted: false,
        dropzoneOptions: {
          url: '#',
          method: "POST",
          maxFilesize: 10,
          acceptedFiles: '.xlsx,.xls,.csv',
          maxFiles: 1,
          addRemoveLinks: true,
          dictDefaultMessage: "Drop Excel file here or click to upload",
          autoProcessQueue: false,
          timeout: 100000000
        }
      };
    },
    computed: {
      ...mapState("appState", ["apiURL"]),
      getUploadURL() {
        return this.apiURL + CERTIFICATE_GENERATE_API + this.certificate?.id;
      },
      isCurrentStepValid() {
        if (this.currentStep === 1) return this.selectedFile;
        if (this.currentStep === 2) {
          if (this.excelData.length === 0) return false;
          if (this.isLoadingUsers) return false;
          const hasOverflow = Object.values(this.overflowStates).some(v => v === true);
          return !hasOverflow;
        }
        return true;
      },
      formattedRows() {
        return this.excelData.map((row, i) => {
          const user = this.users.find(u => u.email.toLowerCase() === row.email.toLowerCase());
          const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
          return { ...row, fullName, userExists: this.isLoadingUsers || !!user };
        });
      }
    },
    watch: {
      visible(newVal) {
        if (newVal) {
          this.initializeDropZone();
          this.resetGeneratingState();
          this.fetchTemplateContent();
        }
      }
    },
    methods: {
      ...mapActions("user", ["getUsersByEmails"]),
      onModalClose(){
        this.resetForm();
        this.invalidUserEmails = [];
        this.$emit('updateModalState', false);
      },
      jumpToStep(step) { 
        if (step <= this.currentStep || (step === this.currentStep + 1 && this.isCurrentStepValid)) this.currentStep = step; 
      },
      nextStep() { 
        if (this.isCurrentStepValid) this.currentStep++; 
      },
      previousStep() { 
        if (this.currentStep > 1) this.currentStep--; 
      },
      initializeDropZone() {
        this.dropzoneOptions.url = this.getUploadURL;
        this.dropzoneOptions.headers = {
          'AuthToken': 'System123',
          'Authorization': `Bearer ${CookieHelpers.getCookie("token")}`,
          'Accept': 'application/json',
        }
      },
      onFileAdded(file) {
        this.selectedFile = null;
        if (!file) return;
        const allowedExtensions = [".csv", ".xlsx", ".xls"];
        const fileName = file.name.toLowerCase();
        const isValid = allowedExtensions.some(ext => fileName.endsWith(ext));
        if (!isValid) {
          this.showError("Invalid format. Allowed formats: CSV, XLSX");
          return;
        }
        this.parseExcelFile(file);
        this.selectedFile = file;
      },
      parseExcelFile(file) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = window.XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = window.XLSX.utils.sheet_to_json(worksheet, { defval: "" });
            const parsedRows = jsonData.map(row => {
              const email = row.email || row.Email || row.EMAIL;
              if (email) {
                return { ...row, email };
              }
              return null;
            }).filter(r => !!r);
            this.excelData = parsedRows;
            await this.loadUsers();
          } catch (err) {
            this.showError("Invalid Excel file. Please upload again.");
          }
        };
        reader.readAsArrayBuffer(file);
      },
      buildUserPlaceholders(user) {
        const system = {
          UserFullName: user.fullName || "",
          CertificateTitle: this.certificate?.title || "",
          Description: this.certificate?.description || "",
        };
        
        return { ...system, ...user };
      },
      async loadUsers() {
        try {
          this.isLoadingUsers = true;
          const emails = this.excelData.map(x => x.email);
          const uniqueEmails = [...new Set(emails)];
          const emailCsv = uniqueEmails.join(",");
          const data = await this.getUsersByEmails(emailCsv);
          this.users = data || [];
        } catch (err) {
          this.showError("Failed to load user details.");
        } finally {
          this.isLoadingUsers = false;
        }
      },
      handleOverflowChange(email, { hasOverflow }) { 
        this.$set(this.overflowStates, email, hasOverflow); 
      },
      onFileRemoved() { 
        this.selectedFile = null; this.excelData = []; this.users = []; 
      },
      onUploadStart() { 
        this.isGenerating = true; this.isCompleted = false; 
      },
      onUploadSuccess(file, response) {
        this.isCompleted = true;
        setTimeout(() => { this.$emit('updateModalState', false); this.resetForm(); }, 2000);
      },
      onUploadError(file, message, xhr) {
        this.isGenerating = false;
        let errorMessage = message;
        try {
          if (xhr && xhr.response) {
            const response = JSON.parse(xhr.response);
            errorMessage = response?.error?.message || "Upload failed. Please try again.";
            this.invalidUserEmails = response?.error?.invalidUserEmails || [];
          } else throw new Error();
        } catch (e) {
            errorMessage = "Upload Failed."
        }
        finally {
            this.showError(errorMessage)
            this.resetForm();
        }
      },
      copyInvalidEmails() {
        const text = this.invalidUserEmails.join('\n');
        navigator.clipboard.writeText(text).then(() => {
          this.$toasted.success("Invalid emails copied to clipboard!", { theme: "outline", position: "bottom-center", duration: 3000 });
          this.invalidUserEmails = [];
        });
      },
      async fetchTemplateContent() {
        try {
          this.isLoadingPreview = true;
          const response = await fetch(this.certificate.templateURL);
          this.templateContent = await response.text();
          const regex = /{{\s*([A-Z][a-zA-Z0-9]*)\s*}}/g;
          const matches = [...this.templateContent.matchAll(regex)];
          this.placeholders = [...new Set(matches.map(m => m[1]))]; 
        } catch (error) {
          this.showError('Failed to fetch template content.');
        } finally {
          this.isLoadingPreview = false;
        }
      },
      showError(message, options = {}) {
        this.$toasted.error(message, { icon: "exclamation-triangle", theme: "outline", position: "bottom-center", singleton: true, duration: 3000, ...options });
      },
      async handleFinish() {
        if (!this.certificate || !this.certificate.title) {
          this.showError("Certificate details are incomplete.");
          return;
        }
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Are you sure you want to generate certificate for "${this.certificate.title}"?`,
          { title: "Confirm Certificate Generation", okTitle: "Yes, Generate", cancelTitle: "Cancel", centered: true, size: "md", okVariant: "issue-certificate" }
        );
        if (!confirmed) return;

        this.$refs.excelDropzone.dropzone.options.url = this.getUploadURL + `?sendEmail=${this.sendEmail}`;
        this.$refs.excelDropzone.processQueue();
      },
      resetForm() {
        this.selectedFile = null;
        this.excelData = [];
        this.users = [];
        this.currentStep = 1;
        this.resetGeneratingState();
        if (this.$refs.excelDropzone) this.$refs.excelDropzone.removeAllFiles();
      },
      resetGeneratingState() { 
        this.isGenerating = false; this.isCompleted = false; 
      }
    }
  };
  </script>
  
<style>
#issue-certificate-modal .modal-body.modal-body {
    display: flex;
    flex-direction: row;
    padding: 0 !important;
    overflow: hidden;
}

#issue-certificate-modal .modal-body .body-container {
    padding: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
}

#issue-certificate-modal .expanded {
    max-width: 96%;
    max-height: 96%;
}

#issue-certificate-modal .expanded .issue-certificate-modal {
    height: 90vh;
}

#issue-certificate-modal-dropzone .dz-default.dz-message {
    margin-top: 4rem;
}

#issue-certificate-modal-dropzone .dz-progress,
#issue-certificate-modal-dropzone .dz-error-message {
    display: none;
}

#issue-certificate-modal-dropzone .dz-preview .dz-details {
    background-color: #4BB543 !important;
}

.btn-issue-certificate {
    background-color: var(--primary-color) !important;
    border-color: var(--primary-color) !important;
    color: #fff !important;
}
</style>

<style scoped>
.modal-content-wrapper {
    display: flex;
    flex: 1;
    margin-right: 1rem;
    position: relative;
}

/* Sidebar */
.step-sidebar {
    display: flex;
    width: 300px;
    flex-shrink: 0;
    background: #f5f5f5;
    padding: 2.5rem 0;
    flex-direction: column;
    justify-content: center;
}

.step-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 0 1rem 2rem;
    border-left: 2px solid #e5e7eb;
    margin: 3.5rem 0;
}

.step-item.active,
.step-item.completed {
    border-left-color: var(--primary-color);
}

.step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e5e7eb;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
}

.step-item.active .step-circle,
.step-item.completed .step-circle {
    background: var(--primary-color);
    color: #fff;
}

.step-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 0.25rem 0;
    color: #374151;
}

.step-item.active .step-title {
    color: var(--primary-color);
}

.step-description {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

/* Main content */
.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.modal-title {
    font-size: 2rem;
    font-weight: 450;
    margin: 2rem;
    text-transform: capitalize;
}

.info-container .step-content {
    padding: 0 2rem;
    overflow: auto;
}

.info-container.expand .step-content {
    height: 60vh;
}

/* Upload box */
.upload-container-wrapper {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
}

.template-dropzone {
    flex: 1;
    width: 100%;
    min-height: 200px;
    border: 2px dashed #e5e7eb;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    transition: border-color 0.3s ease;
}

.template-dropzone:hover {
    border-color: var(--primary-color);
}

.template-info {
    align-self: baseline;
}

/* Buttons */
.download-sample-link {
    color: var(--primary-color);
    text-decoration: underline;
    font-weight: 500;
    cursor: pointer;
}

.allowed-formats {
    font-size: 0.85rem;
    color: #555;
    margin-top: 1rem;
}

.error-row {
    display: flex;
    align-items: baseline;
}

.copy-btn {
    border-radius: .6rem;
    background: transparent;
    border: 1px solid var(--primary-color);
    color: var(--primary-color);
    padding: .4rem 2rem;
}

.copy-btn:hover {
    background: var(--primary-color);
    color: white;
}

.step-navigation {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    padding: 2rem;
}

.step-btn.secondary {
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #374151;
}

.step-btn.secondary:hover {
    background: #f9fafb;
}

.step-btn.secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Close button */
.right-close-panel {
    position: absolute;
    right: 2rem;
    z-index: 20;
}

.right-close-panel .close-button {
    background: none;
    border: none;
    cursor: pointer;
}

/* Generating overlay */
.generating-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.98);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 15;
    backdrop-filter: blur(2px);
    border-radius: 8px;
}

.generating-content {
    text-align: center;
    padding: 2rem;
}

.loading-icon-container {
    margin-bottom: 2rem;
}

.generating-spinner {
    width: 4rem; 
    height: 4rem;
}

.tick-icon {
    position: absolute;
    top: 50%;            
    left: 50%;      
    transform: translate(-50%, -180%);
    background: #4BB543;
    border-radius: 50%;
    width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 2rem;
}

.generating-title {
    font-size: 1.5rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
}

.generating-subtitle {
    color: #6b7280;
    font-size: 1rem;
}

.placeholder-list {
  list-style: none;   
  padding-left: 0;    
  margin: 0;  
}

.preview-list {
    margin: 1rem 0;
    padding-left: 1.5rem;
}

.shimmer-card {
    width: 460px;
    height: 243.6px;
    border-radius: 8px;
    background: linear-gradient(90deg, #f4f4f4 25%, #e9e9e9 50%, #f4f4f4 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    margin: 1rem 0;
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .user-not-found {
    color: #b91c1c;
    font-weight: 500;
  }
</style>
